import { useState, ReactNode, ReactPortal } from 'react'
import { createPortal } from 'react-dom'
import { useSearchBox, useClearRefinements } from 'react-instantsearch'
import { Button, Dialog, DialogTrigger, Modal } from 'react-aria-components'

import AlgoliaDateFilter from './algolia-date-filter'
import AlgoliaFilterList from './algolia-category-list'
import AlgoliaFilterButton from './algolia-filter-button'

type AlgoliaIndexToolbarProps = {
  state: {
    startDateFilter: number | null
    endDateFilter: number | null
  }
  stateCallbacks: {
    setStartDateFilter: (date: number) => void | null
    setEndDateFilter: (date: number) => void | null
  }
}

export default function AlgoliaIndexToolbar({
  state,
  stateCallbacks,
}: AlgoliaIndexToolbarProps): ReactPortal | ReactNode {
  const { query, refine } = useSearchBox()
  const [_, setInputValue] = useState(query)
  const { canRefine, refine: clearRefinement } = useClearRefinements({
    includedAttributes: ['default_genre', 'query'],
  })

  function setQuery(newQuery: string) {
    setInputValue(newQuery)
    refine(newQuery)
  }

  const portalRoot = document.getElementById('react-portal-placeholder')

  const toolbarContent = (
    <form className="w-full flex gap-x-lg max-sm:gap-x-sm">
      <input
        onChange={(event) => setQuery(event.currentTarget.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        maxLength={300}
        type="text"
        placeholder={`Search ${window.POST_TYPE}`}
        className={`
          rounded-full
          w-full
          sm:h-[64px]
          max-sm:h-[54px]
          py-sm
          px-lg
          pl-[60px]
          max-sm:pl-[44px]
          text-lg
          max-md:text-sm
          input-bg--search
          outline-2
          duration-250
          transition-colors
          outline-primary
          placeholder-primary
          hover:bg-line
          focus:text-bg
          focus:bg-primary
        `}
      />

      {window.POST_TYPE === 'events' ? (
        <DialogTrigger>
          <AlgoliaFilterButton />

          <Modal
            isDismissable
            className="w-full max-w-[600px] max-h-[90vh] rounded-md bg-white sm:mx-sm mx-2xs overflow-hidden">
            <Dialog className="flex flex-col h-full max-h-[90vh]">
              <header className="flex items-center justify-between px-lg py-md border-b border-grey-light">
                <span className="text-lg">Filters</span>
                <Button slot="close">
                  <svg
                    width="18"
                    height="18"
                    className="text-black hover:text-gold">
                    <use xlinkHref="#icon-close"></use>
                  </svg>
                  <span className="sr-only">Close filter window</span>
                </Button>
              </header>

              <div className="p-lg overflow-y-auto flex-1">
                <AlgoliaDateFilter
                  heading="Date range"
                  state={state}
                  stateCallbacks={stateCallbacks}
                />

                <AlgoliaFilterList
                  heading="Categories"
                  attribute="default_genre"
                  sortBy={['name:asc']}
                />
              </div>

              <footer className="flex items-center justify-between px-lg py-md border-t border-grey-light flex-shrink-0">
                {canRefine || state.startDateFilter || state.endDateFilter ? (
                  <Button
                    onClick={() => {
                      clearRefinement()

                      if (state.startDateFilter) {
                        stateCallbacks.setStartDateFilter(null)
                      }

                      if (state.endDateFilter) {
                        stateCallbacks.setEndDateFilter(null)
                      }
                    }}
                    className="text-sm hover:text-gold">
                    Clear all
                  </Button>
                ) : (
                  <span></span>
                )}

                <Button
                  className="w-auto btn btn--sm btn--solid py-sm px-sm"
                  slot="close"
                  aria-label="Close filter window return to show results">
                  View results
                </Button>
              </footer>
            </Dialog>
          </Modal>
        </DialogTrigger>
      ) : null}
    </form>
  )

  if (portalRoot) {
    return createPortal(toolbarContent, portalRoot)
  }

  return toolbarContent
}
